import React, { useEffect } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import { useAuth } from "./AuthContext";
import Login from "./components/auth/Login";
import ProtectedRoute from "./ProtectedRoute";
import Home from "./components/pages/Home";
import { Toaster } from "react-hot-toast";
import Register from "./components/auth/Register";
import OnBoarding from "./components/pages/OnBoarding";
import Dashboard from "./components/widgets/Dashboard";
import CryptoExchange from "./components/pages/CryptoExchange";
import Forgot from "./components/auth/Forgot";
import Kyc from "./components/pages/Kyc";
import Support from "./components/pages/Support";
import Chatting from "./components/pages/Chatting";
import NotFound from "./components/widgets/404";
import ForexExchange from "./components/pages/ForexExchange";
import CryptoAssets from "./components/pages/CryptoAssets";
import GoogleAuthentication from "./components/pages/GoogleAuthentication";
import CryptoTradeHistory from "./components/pages/CryptoTradeHistory";
import CryptoTransactionHistory from "./components/pages/CryptoTransactionHistory";
import MobileChart from "./components/partials/MobileChart";
import EquityExchange from "./components/pages/EquityExchange";
import ForexMobile from "./components/partials/ForexMobile";
import UserActivity from "./components/pages/UserActivity";
import ChangePassword from "./components/pages/ChangePassword";
import ForexExchangeEquity from "./components/pages/ForexExchangeEquity";
import TradingViewCryptoMobile from "./components/partials/TradingViewCryptoMobile";
import ForgotUsername from "./components/auth/ForgotUsername";
import { useDispatch } from "react-redux";
import { useGetallUserQuery } from "./redux/userApi";
import { setUserSettings } from "./redux/userSlice";
import Loader from "./util/loader";
import Profile from "./components/pages/Profile";
import PermissionNot from "./components/widgets/PermissionNot";
import EventWeidgit from "./components/partials/EventWeidgit";
import Masters from "./components/pages/Masters";
import MasterProfile from "./components/pages/masterProfile";
import CopierArea from "./components/pages/CopierArea";
import SubScriptionSetup from "./components/pages/SubScriptionSetup";
import MasterProfileSetup from "./components/pages/MasterProfileSetup";
import CopierMasters from "./components/pages/CopierMasters";
import CryptoExchangeFutures from "./components/pages/CryptoExchangeFutures";
import MasterTabs from "./components/elements/MasterTabs";
import { setDefaultCurrency } from "./util/decimalFormatter";
import UnifiedLayout from "./components/pages/UnifiedLayout";
import IbRequest from "./components/pages/IbRequest";
import PartnerArea from "./components/pages/PartnerArea";
import PartnerReferal from "./components/pages/PartnerReferal";
import PartnerReport from "./components/pages/PartnerRepost";
import PartnerResource from "./components/pages/PartnerResource";
import PartnerNetwork from "./components/pages/PartnerNetwork";
import MultiAccount from "./components/pages/MultiAccount";
import NewAccount from "./components/pages/NewAccount";
import About from "./components/pages/About";
import Awards from "./components/pages/Awards";
import Faq from "./components/pages/Faq";
import ContactUs from "./components/pages/ContactUs";
import RiskDisclosure from "./components/pages/RiskDisclosure";
import PrivacyPolicy from "./components/pages/PrivacyPolicy";
import ReturnPolicy from "./components/pages/ReturnPolicy";
import CustomerAgreement from "./components/pages/CustomerAgreement";
import AMLPolicy from "./components/pages/AMLPolicy";
import HelpCenter from "./components/pages/HelpCenter";
import Fees from "./components/pages/Fees";
import Security from "./components/pages/Security";
import LawEnforcement from "./components/pages/LawEnforcement";
import ScrollToTop from "./ScrollToTop";

const App = () => {
  const jwtToken = localStorage.getItem("jwtToken");
  const { login } = useAuth();
  const dispatch = useDispatch();
  const shouldFetchUser = !!jwtToken; // Only fetch if the token exists
  const { data, isLoading } = useGetallUserQuery(undefined, {
    skip: !shouldFetchUser, // Skip the query if the token doesn't exist
  });

  const urlParams = new URLSearchParams(window.location.search);

  useEffect(() => {
    const jwtToken = localStorage.getItem("jwtToken");
    if (jwtToken) {
      login();
      const isLogout = urlParams.get("logout");

      if (isLogout === "true") {
        // Remove authentication tokens or session data
        localStorage.removeItem("jwtToken"); // or whatever token key you're using

        localStorage.clear(); // Redirect to login page without the query parameter
        window.location.replace("/login");
      }
      if (data) {
        dispatch(setUserSettings(data)); // Dispatch action to set user settings in Redux
      }
    }
  }, [jwtToken, login, data, dispatch]);

  useEffect(() => {
    setDefaultCurrency();
  }, []);

  // useEffect(() => {
  //   const jwtToken = localStorage.getItem("jwtToken");
  //   if (jwtToken) {
  //     login();
  //     const urlParams = new URLSearchParams(window.location.search);
  //     const isLogout = urlParams.get("logout");

  //     if (isLogout === "true") {
  //       // Remove authentication tokens or session data
  //       localStorage.removeItem("jwtToken"); // or whatever token key you're using

  //       // Redirect to login page without the query parameter
  //       window.location.replace("/login");
  //     }
  //     if (data) {
  //       dispatch(setUserSettings(data)); // Dispatch action to set user settings in Redux
  //     }
  //   }
  // }, [jwtToken, login, data, dispatch]);

  if (isLoading) {
    // Replace 'Loader' with your actual loading component
    return (
      <div className="loader-center">
        <Loader />
      </div>
    );
  }

  return (
    <div className="App">
      <Router>
      <ScrollToTop /> {/* This triggers on every route change */}

        <Routes>
          <Route path="/" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/masters" element={<Masters />} />
          <Route
            path="/master-profile/:masterUserId"
            element={<MasterProfile />}
          />
          <Route path="/forgot-password" element={<Forgot />} />
          <Route path="/about-us" element={<About />} />
          <Route path="/awards" element={<Awards />} />
          <Route path="/faq" element={<Faq />} />
          <Route path="/risk-disclosure" element={<RiskDisclosure />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/return-policy" element={<ReturnPolicy />} />
          <Route path="/customer-agreement" element={<CustomerAgreement />} />
          <Route path="/aml-policy" element={<AMLPolicy />} />
          <Route path="/help-center" element={<HelpCenter />} />
          <Route path="/fees" element={<Fees />} />
          <Route path="/security" element={<Security />} />
          <Route path="/law-enforcement" element={<LawEnforcement />} />
          <Route path="/contact-us" element={<ContactUs />} />
          <Route path="/forgot-username" element={<ForgotUsername />} />
          <Route
            path="/onboarding"
            element={<ProtectedRoute component=<OnBoarding /> />}
          />
          <Route
            path="/dashboard"
            element={<ProtectedRoute component=<Dashboard /> />}
          />
          {/* {data?.is_crypto_enabled === "yes" ? ( */}
          {/* <Route
            path="/crypto/:firstCoin/:secondCoin"
            element={<CryptoExchange />}
          /> */}
          {/* ) : (
            <Route
              path="/crypto/:firstCoin/:secondCoin"
              element={<ProtectedRoute component=<PermissionNot /> />}
            />
          )} */}
          {/* {data?.is_crypto_future_enabled === "yes" ? (
            <Route
              path="/crypto-futures/:firstCoin/:secondCoin"
              element={<ProtectedRoute component=<CryptoExchangeFutures /> />}
            />
          ) : (
            <Route
              path="/crypto-futures/:firstCoin/:secondCoin"
              element={<ProtectedRoute component=<PermissionNot /> />}
            />
          )} */}
          <Route
            path="/copier-area/:copierUserId/:masterUserId"
            element={<ProtectedRoute component=<CopierArea /> />}
          />
          <Route
            path="/copier-masters"
            element={<ProtectedRoute component=<CopierMasters /> />}
          />
          <Route path="/subscription-setup" element={<SubScriptionSetup />} />
          <Route
            path="/master-profile-setup"
            element={<ProtectedRoute component={<MasterTabs />} />}
          />
          <Route
            path="/profile"
            element={<ProtectedRoute component=<Profile /> />}
          />
          <Route
            path="/mobile-crypto/:firstCoin/:secondCoin"
            element={<TradingViewCryptoMobile />}
          />
          <Route path="/mobile-calander" element={<EventWeidgit />} />
          <Route path="/mobile-forex/:symbol" element={<ForexMobile />} />
          {/* <Route path="/stocks/:symbol" element={<EquityExchange />} /> */}
          {/* {data?.is_fx_enabled === "yes" ? ( */}
          {/* <Route path="/forex/:symbol" element={<ForexExchange />} /> */}
          {/* ) : ( */}
          {/* <Route
              path="/forex/:symbol"
              element={<ProtectedRoute component=<PermissionNot /> />}
            /> */}
          {/* )} */}
          {/* {data?.is_equity_enabled === "yes" ? ( */}
          {/* <Route
            path="/forex-equity/:symbol"
            element={<ForexExchangeEquity />}
          /> */}
          <Route
            path="/trading-dashboard"
            element={<ProtectedRoute component=<UnifiedLayout /> />}
          />
          {/* ) : (
            <Route
              path="/forex-equity/:symbol"
              element={<ProtectedRoute component=<PermissionNot /> />}
            />
          )} */}
          <Route
            path="/verify-identity"
            element={<ProtectedRoute component=<Kyc /> />}
          />
          <Route
            path="/support"
            element={<ProtectedRoute component=<Support /> />}
          />
          <Route
            path="/change-password"
            element={<ProtectedRoute component=<ChangePassword /> />}
          />
          <Route
            path="/account"
            element={<ProtectedRoute component=<CryptoAssets /> />}
          />
          <Route
            path="/two-factory"
            element={<ProtectedRoute component=<GoogleAuthentication /> />}
          />
          <Route
            path="/trade-history"
            element={<ProtectedRoute component=<CryptoTradeHistory /> />}
          />
          <Route
            path="/transaction-history"
            element={<ProtectedRoute component=<CryptoTransactionHistory /> />}
          />
          <Route
            path="/activity"
            element={<ProtectedRoute component=<UserActivity /> />}
          />
          <Route
            path="/Chatting/:ticketId/:status"
            element={<ProtectedRoute component=<Chatting /> />}
          />
          <Route
            path="/partner-program"
            element={<ProtectedRoute component=<IbRequest /> />}
          />
          <Route
            path="/partner-dashboard"
            element={<ProtectedRoute component=<PartnerArea /> />}
          />
          <Route
            path="/partner-referal"
            element={<ProtectedRoute component=<PartnerReferal /> />}
          />
          <Route
            path="/partner-report"
            element={<ProtectedRoute component=<PartnerReport /> />}
          />
          <Route
            path="/partner-resource"
            element={<ProtectedRoute component=<PartnerResource /> />}
          />
          <Route
            path="/partner-network"
            element={<ProtectedRoute component=<PartnerNetwork /> />}
          />{" "}
          <Route
            path="/multi-account"
            element={<ProtectedRoute component=<MultiAccount /> />}
          />
          <Route
            path="/new-account"
            element={<ProtectedRoute component=<NewAccount /> />}
          />
          <Route exact path="*" element={<NotFound />} />
        </Routes>
      </Router>
      <Toaster position="top-right" />
    </div>
  );
};

export default App;
